import React, { useState, useEffect, useContext, useRef } from "react";
import { Button, Form, Row, Spinner, Col, Dropdown } from "react-bootstrap";
import { fullValidation } from "../helper/WebResourceValidators";
import {
  APIWebVisualization,
  APIFileVisualization,
} from "../helper/APIFunctions";
import { UserInfo, VisualCache } from "../helper/Context";
import UploadResultDisplay from "./UploadResultDisplay";
import {
  canUploadVideo,
  getMaxUploadSizeBytes,
  getMaxUploadSizeMB,
  getVisualLifetimeHrs,
} from "../helper/BusinessLogic";
import JSZip from "jszip";
import { shallowCopy, prepBrandProfileList } from "../helper/Utils.js";
const getBrandProfileOptions = (userInfo) => {
  let t = [...prepBrandProfileList(userInfo.branding_profiles, "organization").map(e=>({...e, suffix: "(Organization)"})), ...prepBrandProfileList(userInfo.branding_profiles, "personal").map(e=>({...e, suffix: "(Personal)"})), {"id": "", "profile_name": "None"}];
  t = t.filter( e=>(e.id || e.profile_name == "None"));
  return t;
}
const TitleDesc = (props) => {
  return (
    <>
      <Form.Group controlId={props.idPrefix + "-title"} className="mb-3">
        <Row>
          <Form.Label className="col-sm-3">Title</Form.Label>
          <Form.Control
            type="text"
            value={props.title}
            placeholder="Optional Title for Visual"
            dir="auto"
            onChange={(event) => {
              props.setTitle(event.target.value);
            }}
            disabled={props.disabled}
          />
        </Row>
      </Form.Group>
      <Form.Group controlId={props.idPrefix + "-description"} className="mb-3">
        <Row>
          <Form.Label className="col-sm-3">Description</Form.Label>
          <Form.Control
            as="textarea"
            value={props.desc}
            placeholder="Optional Description for Visual"
            dir="auto"
            onChange={(event) => {
              props.setDesc(event.target.value);
            }}
            disabled={props.disabled}
          />
        </Row>
      </Form.Group>
      {props.brandProfileOptions && props.brandProfileOptions.length >= 2 ? (
        <Form.Group
          controlId={props.idPrefix + "-branding"}
          className="mb-3"
        >
          <Row>
            <Form.Label className="col-sm-3">Branding Profile: </Form.Label>
            <Form.Select
              value={props.selectedBrandProfile}
              onChange={(event) => {
                props.setBrandProfile(event.target.value);
              }}
              disabled={props.disabled}
              className="col-sm-8 w-75"
            >
              {props.brandProfileOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.profile_name} {e.suffix??""}
                </option>
              ))}
            </Form.Select>
          </Row>
        </Form.Group>
      ) : null}
    </>
  );
};

const setWebFieldsetDisabled = (isDisabled) => {
  const t = document.getElementById("snorkle-web-fieldset")
  if (t) {
    t.disabled = isDisabled;
  }
}
export const WebUploadForm = (props) => {
  const [title, setTitle] = useState("");
  const { userInfo } = useContext(UserInfo);
  const [description, setDescription] = useState("");
  const [webResource, setWebResource] = useState("");
  const [validatedSource, setValidatedSource] = useState({}); // empty object for not yet validated, null for validation failed
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadResult, setUploadResult] = useState([]);
  const { visCache, setVisCache } = useContext(VisualCache);
  const [webResourceTouched, setWebResourceTouched] = useState(false);
  const brandProfileOptions = getBrandProfileOptions(userInfo);
  const [brandProfileId, setBrandProfileId] = useState(
    (brandProfileOptions[0] || {}).id
  );
  const webUploadSuccess = (body) => {
    setUploadingCount(i => i - 1);
    const tempVisCache = shallowCopy(visCache);
    tempVisCache[body.id] = body;
    setVisCache(tempVisCache);
    if (typeof props.onVisualAdded === 'function') {
      props.onVisualAdded(body);
    }
    setUploadResult(ur => [...ur, { success: true, payload: body }]);
    props?.setIsUploading?.(false);
    if (props.onlyOneVisual) {
      setWebFieldsetDisabled(false);
    }
  };
  const webUploadFailure = (body) => {
    let err_msg = "";
    if (body.error) {
      err_msg = body.error;
    } else {
      err_msg = body.toString();
    }
    setUploadingCount(i => i - 1);
    setUploadResult(ur => [...ur, { success: false, payload: err_msg }]);
    props?.setIsUploading?.(false);
    if (props.onlyOneVisual) {
      setWebFieldsetDisabled(false);
    }
  };
  useEffect(() => {
    if (props.breadcrumbs && props.baseCrumbs && props.setBreadcrumbs) {
      const len = props.breadcrumbs.length;
      if (
        len !== props.baseCrumbs.length + 1 ||
        props.breadcrumbs[len - 1].url !==
        props.baseCrumbs[props.baseCrumbs.length - 1].url + "/web"
      ) {
        let newBC = [...props.baseCrumbs];
        newBC.push({
          name: "Web Source",
          url: props.baseCrumbs[props.baseCrumbs.length - 1].url + "/web",
        });
        props.setBreadcrumbs(newBC);
      }
    }
  });

  // Lock body scroll while uploading
  useEffect(() => {
    if (uploadingCount > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [uploadingCount]);

  return (
    <>
      {!props.isInPresentation && uploadResult && uploadResult.length && !uploadingCount ? (
        <UploadResultDisplay result={uploadResult[uploadResult.length - 1]} isOrg={props.isOrg} />
      ) : null}

      {/* Full-screen overlay while uploading */}
      {uploadingCount > 0 && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '1rem',
        }}>
          <Spinner animation="border" style={{ width: '3rem', height: '3rem', color: '#60a5fa', borderWidth: '3px' }}>
            <span className="visually-hidden">Uploading...</span>
          </Spinner>
          <p style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
            Creating your visual…
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: 0 }}>
            This may take a moment. Please don't navigate away.
          </p>
        </div>
      )}

      <Form
        className="m-4"
        id="web-resource-form"
        onSubmit={(event) => {
          props?.setIsUploading?.(true);
          setWebFieldsetDisabled(true);
          if (!props.onlyOneVisual) {
            setTimeout(() => setWebFieldsetDisabled(false), 1000);
          }
          if (validatedSource.type == "simple_image") {
            APIFileVisualization(
              validatedSource.id,
              title,
              description,
              webUploadSuccess,
              webUploadFailure,
              false,
              { isSimple: true, branding_profile: brandProfileId }
            );
          } else {
            APIWebVisualization(
              validatedSource.id,
              validatedSource.type,
              title,
              description,
              webUploadSuccess,
              webUploadFailure,
              props.isOrg,
              { hash: validatedSource.hash, branding_profile: brandProfileId }
            );
          }
          setUploadingCount(i => i + 1);
          event.preventDefault();
        }}
      >
        <fieldset id="snorkle-web-fieldset">
          <Form.Group controlId="upload-web-resource" className="mb-3">
            <Row>
              <Form.Label className="col-sm-3">Web Resource</Form.Label>
              <Form.Control
                type="text"
                value={webResource}
                isInvalid={validatedSource === null && webResourceTouched}
                isValid={validatedSource && validatedSource.id}
                onChange={(event) => {
                  const value = event.target.value;
                  setValidatedSource({});
                  setWebResource(value);
                  fullValidation(value, !!props.isLoggedIn)
                    .then((res) => {
                      setValidatedSource(res);
                      if (res.type === "snorkle") {
                        setTitle("");
                        setDescription("");
                      }
                    })
                    .catch((err) => {
                      setValidatedSource(null);
                    });
                }}
                onBlur={(event) => {
                  setWebResourceTouched(true);
                }}
                placeholder="URL for Web Resource"
              />
              <Form.Control.Feedback type="invalid">
                Invalid web resource
              </Form.Control.Feedback>
            </Row>
            <Form.Text>
              Accepts URLs for Sketchfab models
              {!!props.isLoggedIn
                ? ", PDB entries, and other Snorkle visuals"
                : " and PDB entries"}
              .
            </Form.Text>
          </Form.Group>

          {props.isLoggedIn ? (
            <TitleDesc
              title={title}
              setTitle={setTitle}
              desc={description}
              setDesc={setDescription}
              idPrefix="upload-web"
              setBrandProfile={setBrandProfileId}
              brandProfileOptions={
                (validatedSource || {}).type == "snorkle" &&
                  !visCache[validatedSource.id]
                  ? []
                  : brandProfileOptions
              }
              disabled={validatedSource && validatedSource.type === "snorkle" && !visCache[validatedSource.id]}
            />
          ) : null}
          <Button
            type="submit"
            disabled={!validatedSource || !validatedSource.id || uploadingCount > 0}
            id="upload-web-submit-btn"
            style={{ minWidth: '90px' }}
          >
            {uploadingCount > 0 ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading…
              </>
            ) : (props.submitButtonText || "Create")}
          </Button>
          {props.onCancel && <Button variant="secondary" className="ms-2" onClick={props.onCancel}>Cancel</Button>}
          {!props.isLoggedIn ? (
            <p className="form-text">
              Visuals uploaded as a guest will only be accessible for 2 weeks.
            </p>
          ) : null}
        </fieldset>
      </Form>
    </>
  );
};

const setFileFieldsetDisabled = (isDisabled) => {
  const t = document.getElementById("snorkle-file-fieldset")
  if (t) {
    t.disabled = isDisabled;
  }
}

export const FileUploadForm = (props) => {
  const alwaysAcceptedExtensions = [
    ".png",
    ".jpg",
    ".jpeg",
    ".tif",
    ".tiff",
    ".zip",
    ".dcm",
    ".DCM",
    ".czi",
    ".mtl",
    ".obj",
    ".stl",
    ".ply",
    ".dae",
    ".bin",
    ".gltf",
    ".glb",
    ".pdb",
    ".cif",
    ".sdf",
    ".mol",
    ".mol2"
  ];
  const videoExtensions = [
    ".mov",
    ".mp4",
    ".webm",
    ".mkv",
    ".flv",
    ".vob",
    ".ogv",
    ".ogg",
    ".qt",
    ".wmv",
    ".amv",
    ".mp4",
    ".m4v",
    ".mpg",
    ".mp2",
    ".mpeg",
    ".mpe",
  ];
  const modelExtensions = [".obj", ".stl", ".ply", ".dae", ".gltf", ".glb"];
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fileList, setFileList] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadResult, setUploadResult] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [filesValid, setFilesValid] = useState(false);
  const [imageTools, setImageTools] = useState("simple");
  const [isVideo, setIsVideo] = useState(false);
  const { visCache, setVisCache } = useContext(VisualCache);
  const { userInfo, setUserInfo } = useContext(UserInfo);
  const brandProfileOptions = getBrandProfileOptions(userInfo);
  const [brandProfileId, setBrandProfileId] = useState(
    (brandProfileOptions[0] || {}).id
  );
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const [acceptedExtensions, setAcceptedExtensions] = useState(
    alwaysAcceptedExtensions
  );
  const canBeSimple = () => {
    if (!fileList || !fileList[0] || fileList.length > 1) {
      return false;
    }
    const arr = fileList[0].name.split(".");
    if (arr.length == 1) {
      return false;
    }
    const extension = "." + arr[arr.length - 1];
    if ([".png", ".jpg", ".jpeg"].some((e) => e == extension)) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (canUploadVideo(userInfo, props.isOrg)) {
      setAcceptedExtensions(alwaysAcceptedExtensions.concat(videoExtensions));
    } else {
      setAcceptedExtensions(alwaysAcceptedExtensions);
    }
  }, [userInfo]);
  useEffect(() => {
    if (!fileList || !fileList[0]) {
      setFilesValid(false);
      return;
    }
    const arr = fileList[0].name.split(".");
    if (arr.length == 1) {
      setFileError("Unable to identify extension.");
      setFilesValid(false);
      return;
    }
    const extension = "." + arr[arr.length - 1];
    if (!acceptedExtensions.some((e) => e === extension.toLowerCase())) {
      setFileError("Invaild file extension.");
      setFilesValid(false);
      return;
    }
    if (extension === ".zip" && fileList.length > 1) {
      setFileError("Cannot upload multiple .zip files");
      setFilesValid(false);
      return;
    } else if (extension === ".czi" && fileList.length > 1) {
      setFileError("Cannot upload multiple .czi files");
      setFilesValid(false);
      return;
    }
    let currentSize = 0;
    const effectiveTier = userInfo ? userInfo.tier : "guest";
    const sizeLimit = getMaxUploadSizeBytes(effectiveTier);
    let mismatch = false;
    let isModel = false;
    for (let i = 0; i < fileList.length; i++) {
      mismatch |= !fileList[i].name.endsWith(extension);
      const arr = fileList[i].name.split(".");
      const ext = "." + arr[1];
      isModel |= modelExtensions.some((e) => e == ext);
      currentSize += fileList[i].size;
      if (currentSize > sizeLimit) {
        setFileError(
          `Max combined upload size is ${getMaxUploadSizeMB(effectiveTier)}MB`
        );
        setFilesValid(false);
        return;
      }
    }
    if (mismatch && !isModel) {
      setFileError("All files must have the same extension");
      setFilesValid(false);
      return;
    }
    setFileError(null);
    setFilesValid(true);
    setIsVideo(videoExtensions.some((e) => e == extension));
  }, [fileList]);
  useEffect(() => {
    if (props.breadcrumbs && props.baseCrumbs && props.setBreadcrumbs) {
      const len = props.breadcrumbs.length;
      if (
        len !== props.baseCrumbs.length + 1 ||
        props.breadcrumbs[len - 1].url !==
        props.baseCrumbs[props.baseCrumbs.length - 1].url + "/image"
      ) {
        let newBC = [...props.baseCrumbs];
        newBC.push({
          name: "Image Source",
          url: props.baseCrumbs[props.baseCrumbs.length - 1].url + "/image",
        });
        props.setBreadcrumbs(newBC);
      }
    }
  });
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFileList(Array.from(e.dataTransfer.files));
      // Note: clearData() is not allowed in drop event (only in dragstart)
      // The dataTransfer is automatically cleared after drop completes
    }
  };

  const onZoneClick = () => {
    inputRef.current.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileList(Array.from(e.target.files));
    }
  };

  const fileUploadSuccess = (body) => {
    setUploadingCount(i => i - 1);
    const tempVisCache = shallowCopy(visCache);
    tempVisCache[body.id] = body;
    setVisCache(tempVisCache);
    if (typeof props.onVisualAdded === 'function') {
      props.onVisualAdded(body);
    }
    setUploadResult((ur) => {
      const t = [...ur, { success: true, payload: body }];
      return t;
    });
    props?.setIsUploading?.(false);
    if (props.onlyOneVisual) {
      setFileFieldsetDisabled(false);
    }
  };
  const fileUploadFailure = (body) => {
    let err_msg = "";
    if (body.error) {
      err_msg = body.error;
    } else {
      err_msg = body.toString();
    }
    setUploadingCount(i => i - 1);
    props?.setIsUploading?.(false);
    setFileFieldsetDisabled(false);
  };
  // Lock body scroll while uploading
  useEffect(() => {
    if (uploadingCount > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [uploadingCount]);

  return (
    <>
      {!props.isInPresentation && uploadResult && uploadResult.length && !uploadingCount ? (
        <UploadResultDisplay result={uploadResult[uploadResult.length - 1]} isOrg={props.isOrg} />
      ) : null}

      {/* Full-screen overlay while uploading */}
      {uploadingCount > 0 && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '1rem',
        }}>
          <Spinner animation="border" style={{ width: '3rem', height: '3rem', color: '#60a5fa', borderWidth: '3px' }}>
            <span className="visually-hidden">Uploading...</span>
          </Spinner>
          <p style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
            Uploading your file…
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: 0 }}>
            This may take a few minutes. Please don't navigate away.
          </p>
        </div>
      )}

      <Form
        className="m-4"
        id="file-resource-from"
        onSubmit={(event) => {
          props?.setIsUploading?.(true);
          setFileFieldsetDisabled(true);
          if (fileList.length > 1) {
            let zip = JSZip();
            for (let i = 0; i < fileList.length; i++) {
              zip.file(fileList[i].name, fileList[i]);
            }
            zip
              .generateAsync({ type: "blob" })
              .then((blob) => {
                APIFileVisualization(
                  blob,
                  title,
                  description,
                  fileUploadSuccess,
                  fileUploadFailure,
                  props.isOrg,
                  { filename: fileList[0].name.split(".")[0]+".zip", branding_profile: brandProfileId }
                );
              })
              .catch(() => {
                setUploadResult(ur => [...ur, {
                  success: false,
                  payload: "Unable to upload selected files.",
                }]);
                setUploadingCount(i => i - 1);
              });
          } else {
            APIFileVisualization(
              fileList[0],
              title,
              description,
              fileUploadSuccess,
              fileUploadFailure,
              props.isOrg,
              {
                image_tools: imageTools||"simple",
                branding_profile: brandProfileId,
              }
            );
          }
          setUploadingCount(i => i + 1);
          event.preventDefault();
        }}
      >
        <fieldset id="snorkle-file-fieldset">
          <Form.Group controlId="upload-file-resource" className="mb-3">
            <Row>
              <Form.Label className="col-sm-3">Your File</Form.Label>
              {fileError && <div className="text-danger">{fileError}</div>}
              <Col sm={9}>
                <div
                  className={`file-drop-zone ${isDragging ? 'drag-over' : ''} ${props.lightTheme ? 'text-dark' : ''}`}
                  style={props.lightTheme ? { borderColor: '#6c757d' } : {}}
                  onClick={onZoneClick}
                  onDragEnter={handleDragIn}
                  onDragLeave={handleDragOut}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    id="file-upload-input"
                    accept={acceptedExtensions.join(', ')}
                    multiple
                    onChange={handleFileChange}
                    disabled={uploadingCount > 0}
                  />
                  <p className={`drop-zone-text ${props.lightTheme ? 'text-dark' : ''}`}>Drag and drop files here</p>
                  <span className="btn">Choose Files</span>
                </div>

                {fileList.length > 0 && (
                  <div className={`file-list-container ${props.lightTheme ? 'text-dark' : ''}`}>
                    <strong>Selected:</strong>
                    <ul>
                      {fileList.map((file, i) => <li key={i}>{file.name}</li>)}
                    </ul>
                  </div>
                )}

                <Form.Text muted>
                  For static images and volumes: accepts, compressed or raw, .png,
                  .jpg, .jpeg, .tif, .tiff, .czi, .dcm, .stl, .glb, .obj, .ply, and
                  zipped archives of .tif, .tiff, .dcm files; for videos (premium
                  feature): accepts: .mov, .mp4, .webm, .mkv, .flv, .vob, .ogv,
                  .ogg, .qt, .wmv, .amv, .mp4, .m4v, .mpg, .mp2, .mpeg, .mpe. Upload
                  file size is limited to {getMaxUploadSizeMB(userInfo ? userInfo.tier : 'free')}MB for
                  your account tier.
                </Form.Text>
              </Col>
            </Row>
          </Form.Group>
          {props.isLoggedIn ? (
            <TitleDesc
              title={title}
              setTitle={setTitle}
              desc={description}
              setDesc={setDescription}
              brandProfileOptions={!isVideo ? brandProfileOptions : []}
              setBrandProfile={setBrandProfileId}
              idPrefix="upload-local"
            />
          ) : null}
          {canBeSimple() ? (
            <Form.Group controlId="upload-image-tools" className="mb-3">
              <Row>              <Form.Label>Image Tools</Form.Label>
                <Form.Select value={imageTools} onChange={(e) => setImageTools(e.target.value)}>
                  <option value="simple">Simple</option>
                  <option value="channel_anaylsis">Seperate Channel Analysis</option>
                  <option value="deep_zoom">Deep Zoom</option>
                </Form.Select>
              </Row>
            </Form.Group>
          ) : null}
          <Button
            type="submit"
            disabled={!filesValid || uploadingCount > 0}
            id="upload-file-submit-btn"
            style={{ minWidth: '90px' }}
          >
            {uploadingCount > 0 ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading…
              </>
            ) : (props.submitButtonText || "Create")}
          </Button>
          {props.onCancel && <Button variant="secondary" className="ms-2" onClick={props.onCancel}>Cancel</Button>}
          {!props.isLoggedIn ? (
            <p className="form-text">
              Visuals uploaded as a guest will only be accessible for{" "}
              {getVisualLifetimeHrs("guest")} hours.
            </p>
          ) : null}
        </fieldset>
      </Form>
    </>
  );
};
